/**
 * PhysicsLab.jsx — Three rigs:
 *   • FLAT CIRCLE    — friction supplies grip:  v_max = √(μ·g·r)
 *   • VERTICAL LOOP  — gravity rules the top:  v_top ≥ √(g·r), entry ≥ √(5·g·r)
 *   • LINEAR SUVAT   — sled on a straight corridor, u/a/s sliders, live v-t graph
 */
import { useRef, useState, useCallback, useEffect } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useGame } from "./store";
import * as Sound from "./sound";
import { initAudio } from "./sound";

const G = 10;

// ─── Circular / Loop 3D scene ─────────────────────────────────────
function LabScene({ r, mu, vertical, runState, onResult }) {
  const cartRef = useRef();
  const simRef = useRef({ mode: "idle", phi: 0, v: 0, pos: null, vel: null, runId: 0 });

  useFrame((st3, dtRaw) => {
    const dt = Math.min(dtRaw, 0.05);
    const sim = simRef.current;

    if (runState.runId !== sim.runId) {
      sim.runId = runState.runId;
      sim.mode = runState.running ? "run" : "idle";
      sim.phi = vertical ? 0 : -Math.PI / 2;
      sim.v = runState.v;
      sim.pos = null;
      sim.vel = null;
    }

    if (!cartRef.current) return;

    if (vertical) {
      if (sim.mode === "run") {
        Sound.setEngine(true, sim.v);
        const v0 = runState.v;
        const h = r * (1 - Math.cos(sim.phi));
        const v2 = v0 * v0 - 2 * G * h;
        if (v2 <= 0.2 && sim.phi < Math.PI * 0.98) {
          sim.mode = "idle"; Sound.setEngine(false); onResult("rollback"); return;
        }
        sim.v = Math.sqrt(Math.max(0.01, v2));
        sim.phi += (sim.v / r) * dt;
        const x = r * Math.sin(sim.phi);
        const y = r - r * Math.cos(sim.phi);
        cartRef.current.position.set(x, y + 1.0, 0);
        cartRef.current.rotation.z = sim.phi;
        const cosPhi = Math.cos(sim.phi);
        if (cosPhi < 0 && v2 / r < G * -cosPhi) {
          sim.mode = "slide";
          sim.pos = new THREE.Vector3(x, y + 1.0, 0);
          sim.vel = new THREE.Vector3(Math.cos(sim.phi), Math.sin(sim.phi), 0).multiplyScalar(sim.v);
          Sound.setEngine(false); onResult("slide");
        }
        if (sim.phi > 2 * Math.PI * 2) { sim.mode = "idle"; Sound.setEngine(false); onResult("hold"); }
      } else if (sim.mode === "slide") {
        sim.vel.y -= G * dt;
        sim.pos.add(sim.vel.clone().multiplyScalar(dt));
        cartRef.current.position.copy(sim.pos);
        cartRef.current.rotation.z += 3 * dt;
        if (sim.pos.y < 0.6) { sim.mode = "fell"; Sound.playSplash(); }
      } else if (sim.mode === "idle" && !runState.running) {
        cartRef.current.position.set(0, 1.0, 0);
        cartRef.current.rotation.set(0, 0, 0);
      }
      const cam = st3.camera;
      cam.position.lerp(new THREE.Vector3(0, r + 4, r * 2.4 + 36), 0.06);
      cam.lookAt(0, r * 0.95, 0);
    } else {
      const v = runState.v;
      const vmax = Math.sqrt(mu * G * r);
      if (sim.mode === "run") {
        Sound.setEngine(true, v);
        sim.phi += (v / Math.max(5, r)) * dt;
        const x = Math.cos(sim.phi) * r;
        const z = Math.sin(sim.phi) * r;
        cartRef.current.position.set(x, 1.0, z);
        cartRef.current.rotation.y = -sim.phi;
        if (v > vmax * 1.02 && sim.phi > -Math.PI / 2 + Math.PI * 0.5) {
          sim.mode = "slide";
          sim.pos = new THREE.Vector3(x, 1.0, z);
          sim.vel = new THREE.Vector3(-Math.sin(sim.phi), 0, Math.cos(sim.phi)).multiplyScalar(v);
          Sound.setEngine(false); onResult("slide");
        }
        if (sim.phi > -Math.PI / 2 + 4 * Math.PI) { sim.mode = "idle"; Sound.setEngine(false); onResult("hold"); }
      } else if (sim.mode === "slide") {
        sim.vel.y -= G * dt;
        sim.pos.add(sim.vel.clone().multiplyScalar(dt));
        cartRef.current.position.copy(sim.pos);
        cartRef.current.rotation.x += 2.5 * dt;
        if (sim.pos.y < -38) { sim.mode = "fell"; Sound.playSplash(); }
      } else if (sim.mode === "idle" && !runState.running) {
        cartRef.current.position.set(0, 1.0, -r);
        cartRef.current.rotation.set(0, Math.PI, 0);
      }
      const cam = st3.camera;
      cam.position.lerp(new THREE.Vector3(0, r * 1.1 + 22, r * 1.9 + 28), 0.06);
      cam.lookAt(0, 0, 0);
    }
  });

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[60, 100, 40]} intensity={1.1} />
      {vertical ? (
        <>
          <mesh position={[0, r, 0]}>
            <torusGeometry args={[r, 0.7, 12, 96]} />
            <meshStandardMaterial color="#39424e" />
          </mesh>
          <mesh position={[0, r, 0]}>
            <torusGeometry args={[r, 0.12, 8, 96]} />
            <meshStandardMaterial color="#ffd23f" emissive="#5b4a00" />
          </mesh>
          <mesh position={[-6, 0.3, 0]}>
            <boxGeometry args={[12, 0.5, 3]} />
            <meshStandardMaterial color="#00ffaa" opacity={0.6} transparent />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]}>
            <circleGeometry args={[r * 3 + 60, 64]} />
            <meshStandardMaterial color="#1d3a24" />
          </mesh>
        </>
      ) : (
        <>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.02, 0]}>
            <ringGeometry args={[Math.max(2, r - 2.4), r + 2.4, 96]} />
            <meshStandardMaterial color="#39424e" side={THREE.DoubleSide} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.04, 0]}>
            <ringGeometry args={[Math.max(1, r - 0.25), r + 0.25, 96]} />
            <meshStandardMaterial color="#ffd23f" side={THREE.DoubleSide} />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.05, 0]}>
            <circleGeometry args={[r + 18, 64]} />
            <meshStandardMaterial color="#1d3a24" />
          </mesh>
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -8, 0]}>
            <circleGeometry args={[r * 4 + 120, 64]} />
            <meshStandardMaterial color="#0e2f4f" />
          </mesh>
          <mesh position={[Math.cos(Math.PI / 4) * (r / 2), 0.4, Math.sin(Math.PI / 4) * (r / 2)]}
                rotation={[0, -Math.PI / 4, 0]}>
            <boxGeometry args={[r, 0.15, 0.15]} />
            <meshStandardMaterial color="#3eb6ff" />
          </mesh>
          <mesh position={[0, 0.8, 0]}>
            <sphereGeometry args={[0.9, 16, 16]} />
            <meshStandardMaterial color="#3eb6ff" emissive="#1a5b80" />
          </mesh>
        </>
      )}
      <group ref={cartRef}>
        <mesh>
          <boxGeometry args={[3.4, 1.6, 2.0]} />
          <meshStandardMaterial color="#ff4d6d" />
        </mesh>
        <mesh position={[0, 1.1, 0]}>
          <boxGeometry args={[1.8, 0.8, 1.6]} />
          <meshStandardMaterial color="#ffd23f" />
        </mesh>
      </group>
    </>
  );
}

// ─── SUVAT 3D Scene ───────────────────────────────────────────────
// corridorLen = world units (fixed 220). simState.x is in metres.
// scaleFactor converts metres → world units: worldX = (simX / sMetres) * corridorLen
function SuvatScene({ simState, sMetres }) {
  const sledRef = useRef();
  const WORLD_LEN = 220; // fixed world corridor length in units

  useFrame((st3) => {
    if (!sledRef.current) return;
    const simX = simState.current?.x ?? 0;
    // Map physics metres → world units
    const worldX = sMetres > 0 ? (simX / sMetres) * WORLD_LEN : 0;
    sledRef.current.position.x = worldX;

    // Cinematic chase camera: behind and slightly above, looking ahead
    const cam = st3.camera;
    const behind = new THREE.Vector3(worldX - 22, 8, 18);
    const lookAt = new THREE.Vector3(worldX + 30, 1.5, 0);
    cam.position.lerp(behind, 0.08);
    cam.lookAt(lookAt);
  });

  // Marker interval: show 8 markers evenly across corridor
  const MARKER_COUNT = 8;
  const markerStep = WORLD_LEN / MARKER_COUNT;
  const mMetreStep = sMetres / MARKER_COUNT;

  return (
    <>
      <ambientLight intensity={0.6} color="#ffeedd" />
      <directionalLight position={[100, 200, 60]} intensity={1.6} color="#fff8e7" castShadow
        shadow-mapSize-width={2048} shadow-mapSize-height={2048}
        shadow-camera-far={600} shadow-camera-left={-150} shadow-camera-right={300}
        shadow-camera-top={80} shadow-camera-bottom={-80} />
      <directionalLight position={[-60, 80, -80]} intensity={0.3} color="#bfdbfe" />
      <hemisphereLight skyColor="#87ceeb" groundColor="#3a7d44" intensity={0.4} />

      {/* Sky dome */}
      <mesh position={[110, 0, 0]}>
        <sphereGeometry args={[450, 32, 16]} />
        <meshStandardMaterial color="#60b8e8" side={THREE.BackSide} />
      </mesh>
      {/* Sun disc */}
      <mesh position={[200, 120, -150]}>
        <sphereGeometry args={[18, 16, 16]} />
        <meshStandardMaterial color="#fff8b0" emissive="#ffe066" emissiveIntensity={1.5} />
      </mesh>

      {/* Ground — wide green field */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[110, -0.52, 0]} receiveShadow>
        <planeGeometry args={[WORLD_LEN + 80, 120]} />
        <meshStandardMaterial color="#4a9e5c" roughness={0.92} metalness={0} />
      </mesh>
      {/* Track bed — tarmac strip */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[110, -0.48, 0]} receiveShadow>
        <planeGeometry args={[WORLD_LEN + 8, 9]} />
        <meshStandardMaterial color="#2d3748" roughness={0.88} />
      </mesh>
      {/* Centre line */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[110, -0.46, 0]}>
        <planeGeometry args={[WORLD_LEN, 0.4]} />
        <meshStandardMaterial color="#fbbf24" emissive="#78350f" emissiveIntensity={0.2} />
      </mesh>

      {/* Rails */}
      {[-2.0, 2.0].map((z, i) => (
        <mesh key={i} position={[110, -0.18, z]} castShadow receiveShadow>
          <boxGeometry args={[WORLD_LEN + 8, 0.22, 0.4]} />
          <meshStandardMaterial color="#94a3b8" metalness={0.85} roughness={0.18} />
        </mesh>
      ))}
      {/* Rail emissive strip (maglev blue) */}
      {[-2.0, 2.0].map((z, i) => (
        <mesh key={`e${i}`} position={[110, -0.04, z]}>
          <boxGeometry args={[WORLD_LEN + 8, 0.08, 0.18]} />
          <meshStandardMaterial color="#00bcd4" emissive="#006064" emissiveIntensity={1.8} />
        </mesh>
      ))}

      {/* Distance marker boards */}
      {Array.from({ length: MARKER_COUNT + 1 }, (_, i) => {
        const wx = i * markerStep;
        const mLabel = Math.round(i * mMetreStep);
        const isStart = i === 0;
        const isEnd = i === MARKER_COUNT;
        return (
          <group key={i} position={[wx, 0, -7]}>
            {/* Pole */}
            <mesh position={[0, 3, 0]} castShadow>
              <boxGeometry args={[0.22, 6, 0.22]} />
              <meshStandardMaterial color="#374151" metalness={0.4} roughness={0.7} />
            </mesh>
            {/* Board */}
            <mesh position={[0, 6.3, 0]} castShadow>
              <boxGeometry args={[4.5, 1.6, 0.18]} />
              <meshStandardMaterial
                color={isStart ? "#16a34a" : isEnd ? "#dc2626" : "#1e3a5f"}
                emissive={isStart ? "#166534" : isEnd ? "#991b1b" : "#0c1a2e"}
                emissiveIntensity={0.3}
              />
            </mesh>
            {/* Distance text approximation — coloured sub-strip */}
            <mesh position={[0, 6.28, 0.1]}>
              <boxGeometry args={[3.8, 1.0, 0.05]} />
              <meshStandardMaterial
                color={isStart ? "#4ade80" : isEnd ? "#f87171" : "#60a5fa"}
                emissive={isStart ? "#22c55e" : isEnd ? "#ef4444" : "#3b82f6"}
                emissiveIntensity={0.5}
              />
            </mesh>
          </group>
        );
      })}

      {/* Finish arch */}
      <group position={[WORLD_LEN, 0, 0]}>
        {[-6, 6].map((z, i) => (
          <mesh key={i} position={[0, 7, z]} castShadow>
            <boxGeometry args={[0.7, 14, 0.7]} />
            <meshStandardMaterial color="#b91c1c" metalness={0.3} roughness={0.6} />
          </mesh>
        ))}
        <mesh position={[0, 14.2, 0]} castShadow>
          <boxGeometry args={[0.7, 0.7, 12.7]} />
          <meshStandardMaterial color="#b91c1c" metalness={0.3} roughness={0.6} />
        </mesh>
        {/* Pennant flags */}
        {["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#ec4899"].map((c, i) => (
          <mesh key={i} position={[0, 12.5 - i * 0.5, -5.2 + i * 2.2]}>
            <boxGeometry args={[0.12, 1.2, 2.0]} />
            <meshStandardMaterial color={c} emissive={c} emissiveIntensity={0.3} />
          </mesh>
        ))}
        {/* Finish line on ground */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.44, 0]}>
          <planeGeometry args={[2, 9]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
      </group>

      {/* Treeline: left side */}
      {Array.from({ length: 18 }, (_, i) => {
        const wx = i * (WORLD_LEN / 17);
        const h = 8 + Math.sin(i * 3.7) * 3;
        return (
          <group key={i} position={[wx, 0, -22]}>
            <mesh position={[0, h / 2, 0]} castShadow>
              <cylinderGeometry args={[0.55, 0.7, h * 0.35, 6]} />
              <meshStandardMaterial color="#5c3d1e" roughness={0.95} />
            </mesh>
            <mesh position={[0, h * 0.72, 0]} castShadow>
              <coneGeometry args={[3.5 + Math.sin(i) * 0.8, h * 0.7, 7]} />
              <meshStandardMaterial color={i % 3 === 0 ? "#2d6a2d" : i % 3 === 1 ? "#3a8a3a" : "#226622"} roughness={0.85} />
            </mesh>
          </group>
        );
      })}
      {/* Treeline: right side */}
      {Array.from({ length: 14 }, (_, i) => {
        const wx = i * (WORLD_LEN / 13);
        const h = 7 + Math.cos(i * 2.9) * 2.5;
        return (
          <group key={i} position={[wx, 0, 20]}>
            <mesh position={[0, h / 2, 0]}>
              <cylinderGeometry args={[0.5, 0.65, h * 0.3, 6]} />
              <meshStandardMaterial color="#6b4423" roughness={0.95} />
            </mesh>
            <mesh position={[0, h * 0.68, 0]}>
              <coneGeometry args={[3.0 + Math.cos(i) * 0.6, h * 0.65, 7]} />
              <meshStandardMaterial color={i % 2 === 0 ? "#2a5e2a" : "#3d7a3d"} roughness={0.85} />
            </mesh>
          </group>
        );
      })}

      {/* Mountain range in background */}
      {[
        { x: -20, z: -150, h: 90, w: 80 },
        { x: 80, z: -170, h: 120, w: 100 },
        { x: 200, z: -155, h: 100, w: 90 },
        { x: 300, z: -165, h: 80, w: 70 },
      ].map((m, i) => (
        <mesh key={i} position={[m.x, m.h / 2 - 5, m.z]}>
          <coneGeometry args={[m.w / 2, m.h, 5]} />
          <meshStandardMaterial color={i % 2 === 0 ? "#8b7e74" : "#6b6560"} roughness={0.95} />
        </mesh>
      ))}
      {/* Snow caps */}
      {[
        { x: -20, y: 78, z: -150 },
        { x: 80, y: 100, z: -170 },
        { x: 200, y: 88, z: -155 },
      ].map((m, i) => (
        <mesh key={i} position={[m.x, m.y, m.z]}>
          <coneGeometry args={[18, 28, 5]} />
          <meshStandardMaterial color="#f0f4f8" roughness={0.7} />
        </mesh>
      ))}

      {/* Spectator stands */}
      {[40, 110, 180].map((wx, i) => (
        <group key={i} position={[wx, 0, 14]}>
          {[0, 1, 2].map((tier) => (
            <mesh key={tier} position={[0, tier * 2.2 + 1.1, tier * 1.8]}>
              <boxGeometry args={[18, 2, 3.5]} />
              <meshStandardMaterial
                color={tier % 2 === 0 ? "#dc2626" : "#1d4ed8"}
                roughness={0.8} metalness={0.1}
              />
            </mesh>
          ))}
        </group>
      ))}

      {/* Sled — detailed with nose, fins, glow */}
      <group ref={sledRef} position={[0, 0.75, 0]}>
        {/* Main body */}
        <mesh castShadow>
          <boxGeometry args={[5.0, 1.1, 2.4]} />
          <meshStandardMaterial color="#d1d5db" metalness={0.88} roughness={0.12} />
        </mesh>
        {/* Nose fairing */}
        <mesh position={[3.0, 0.05, 0]} rotation={[0, 0, -Math.PI / 2]} castShadow>
          <coneGeometry args={[0.72, 2.0, 7]} />
          <meshStandardMaterial color="#9ca3af" metalness={0.9} roughness={0.1} />
        </mesh>
        {/* Cockpit canopy */}
        <mesh position={[0.4, 0.82, 0]} castShadow>
          <boxGeometry args={[2.2, 0.7, 1.6]} />
          <meshStandardMaterial color="#7dd3fc" transparent opacity={0.55} metalness={0.95} roughness={0.04} />
        </mesh>
        {/* Maglev underbelly glow */}
        <mesh position={[0, -0.6, 0]}>
          <boxGeometry args={[4.5, 0.14, 2.0]} />
          <meshStandardMaterial color="#00e5ff" emissive="#006080" emissiveIntensity={3.0} />
        </mesh>
        {/* Rear fins */}
        {[-0.9, 0.9].map((z, i) => (
          <mesh key={i} position={[-2.2, 0.55, z]} castShadow>
            <boxGeometry args={[0.12, 1.0, 0.7]} />
            <meshStandardMaterial color="#60a5fa" emissive="#1d4ed8" emissiveIntensity={0.7} metalness={0.8} />
          </mesh>
        ))}
        {/* Engine nozzle glow */}
        <mesh position={[-2.7, 0.0, 0]}>
          <sphereGeometry args={[0.45, 8, 8]} />
          <meshStandardMaterial color="#f97316" emissive="#ea580c" emissiveIntensity={3.5} />
        </mesh>
      </group>

      <fog attach="fog" args={["#b8ddf0", 180, 500]} />
    </>
  );
}

// ─── SUVAT v-t canvas ─────────────────────────────────────────────
function SuvatGraph({ history }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const PAD = { l: 38, r: 10, t: 10, b: 28 };
    const plotW = W - PAD.l - PAD.r;
    const plotH = H - PAD.t - PAD.b;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "rgba(10,15,30,0.96)";
    ctx.roundRect(0, 0, W, H, 10);
    ctx.fill();

    if (history.length < 2) return;

    const vMax = Math.max(10, ...history.map((p) => p.v * 1.1));
    const tMax = history[history.length - 1].t || 1;

    const tx = (t) => PAD.l + (t / tMax) * plotW;
    const ty = (v) => PAD.t + plotH - (v / vMax) * plotH;

    // Grid
    ctx.strokeStyle = "rgba(255,255,255,0.07)";
    ctx.lineWidth = 1;
    for (let vl = 0; vl <= vMax; vl += Math.ceil(vMax / 6)) {
      const y = ty(vl);
      ctx.beginPath(); ctx.moveTo(PAD.l, y); ctx.lineTo(W - PAD.r, y); ctx.stroke();
      ctx.fillStyle = "rgba(148,163,184,0.8)";
      ctx.font = "9px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(vl.toFixed(0), PAD.l - 3, y + 3);
    }

    // Area shading
    ctx.beginPath();
    ctx.moveTo(tx(history[0].t), ty(0));
    history.forEach((p) => ctx.lineTo(tx(p.t), ty(p.v)));
    ctx.lineTo(tx(history[history.length - 1].t), ty(0));
    ctx.closePath();
    ctx.fillStyle = "rgba(62,182,255,0.12)";
    ctx.fill();

    // v-t line
    ctx.beginPath();
    ctx.moveTo(tx(history[0].t), ty(history[0].v));
    history.forEach((p) => ctx.lineTo(tx(p.t), ty(p.v)));
    ctx.strokeStyle = "#3eb6ff";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.setLineDash([]);
    ctx.stroke();

    // Axes
    ctx.strokeStyle = "rgba(255,255,255,0.3)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(PAD.l, PAD.t); ctx.lineTo(PAD.l, H - PAD.b);
    ctx.lineTo(W - PAD.r, H - PAD.b); ctx.stroke();

    // Labels
    ctx.fillStyle = "rgba(148,163,184,0.9)";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("time (s) →", W / 2, H - 4);
    ctx.save();
    ctx.translate(11, H / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText("v (m/s)", 0, 0);
    ctx.restore();

    // Current v
    const curV = history[history.length - 1]?.v ?? 0;
    ctx.fillStyle = "#fff";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(`${curV.toFixed(1)} m/s`, W - PAD.r - 2, PAD.t + 12);
  });

  return (
    <canvas ref={canvasRef} width={320} height={140}
      style={{ borderRadius: 10, display: "block", margin: "0 auto" }} />
  );
}

// ─── SUVAT Rig component ──────────────────────────────────────────
function SuvatRig({ labParams }) {
  const [u, setU] = useState(labParams?.suvatU ?? 0);
  const [a, setA] = useState(labParams?.suvatA ?? 4);
  const [s, setS] = useState(labParams?.suvatS ?? 200);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // simStateRef.x is in METRES (physics space).
  // SuvatScene converts metres → world units internally using sMetres prop.
  const simStateRef = useRef({ x: 0 });
  const historyRef = useRef([]);
  const [history, setHistory] = useState([]);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(null);
  // Track current s used in ongoing sim (so camera knows scale)
  const simSRef = useRef(s);

  // Derived answers
  const vFinal = Math.sqrt(Math.max(0, u * u + 2 * a * s));
  const tTotal = a !== 0 ? (vFinal - u) / a : (u > 0 ? s / u : Infinity);
  const stopDist = u > 0 && a < 0 ? (u * u) / (2 * Math.abs(a)) : null;

  // Time-stretch factor: simulation runs 4× faster than real time so a 20s
  // journey takes ~5s of screen time — still shows the full trajectory.
  const TIME_STRETCH = 4;

  const runSim = useCallback((uVal, aVal, sVal) => {
    setRunning(true);
    setResult(null);
    historyRef.current = [];
    simSRef.current = sVal;
    simStateRef.current = { x: 0 };
    lastTimeRef.current = null;
    initAudio();
    Sound.setMaglevHum(true, uVal);

    let physX = 0, physV = uVal, physT = 0;

    const step = (timestamp) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      // wall-clock dt, then scale by TIME_STRETCH for physics
      const wallDt = Math.min((timestamp - lastTimeRef.current) / 1000, 0.05);
      lastTimeRef.current = timestamp;
      const dt = wallDt * TIME_STRETCH;

      physV = Math.max(0, physV + aVal * dt);
      physX = Math.min(sVal, physX + physV * dt);
      physT += dt;

      // Publish x in metres — scene maps it to world coords
      simStateRef.current = { x: physX };
      historyRef.current.push({ t: physT, v: physV });
      if (historyRef.current.length > 200) historyRef.current = historyRef.current.slice(-200);
      setHistory([...historyRef.current]);

      Sound.setMaglevHum(true, physV);

      if (physX >= sVal || (physV <= 0.01 && aVal <= 0)) {
        setRunning(false);
        Sound.setMaglevHum(false);
        if (physV > 0.5) {
          Sound.playChime();
          setResult({ ok: true, finalV: physV, dist: physX, time: physT });
        } else {
          setResult({ ok: false, finalV: 0, dist: physX, time: physT });
        }
        return;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      Sound.setMaglevHum(false);
    };
  }, []);

  const fmt = (n, d = 1) => (isFinite(n) ? Number(n).toFixed(d) : "∞");

  return (
    <div style={{ display: "flex", gap: 0, height: "100%", flex: 1 }}>
      {/* 3D viewport */}
      <div style={{ flex: 1, position: "relative" }}>
        <Canvas camera={{ position: [-22, 8, 18], fov: 65, near: 0.1, far: 1000 }} shadows>
          <SuvatScene simState={simStateRef} sMetres={simSRef.current || s} />
        </Canvas>

        <div style={{ position: "absolute", top: 14, left: 16, color: "#fff", pointerEvents: "none" }}>
          <div style={{ fontSize: 11, letterSpacing: 4, color: "#00e5ff", fontWeight: 700 }}>🚄 LINEAR CORRIDOR RIG</div>
          <div style={{ fontSize: 17, fontWeight: 800 }}>SUVAT — Straight-Line Motion</div>
        </div>

        {/* Result banner */}
        {result && (
          <div style={{
            position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)",
            maxWidth: "min(500px, 80%)", padding: "10px 16px", borderRadius: 10,
            fontWeight: 700, fontSize: 13, textAlign: "center",
            background: result.ok ? "rgba(0,40,25,0.92)" : "rgba(45,8,16,0.92)",
            border: `1px solid ${result.ok ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.5)"}`,
            color: result.ok ? "#00ffaa" : "#ff8a9b",
          }}>
            {result.ok
              ? `✅ Reached ${fmt(s)} m in ${fmt(result.time)} s — final speed ${fmt(result.finalV)} m/s`
              : `↩ STOPPED at ${fmt(result.dist, 0)} m — deceleration exceeded initial speed`}
          </div>
        )}
      </div>

      {/* Control panel */}
      <div style={{
        width: 340, background: "rgba(10,12,24,0.97)", overflowY: "auto",
        borderLeft: "1px solid rgba(255,255,255,0.12)",
        padding: "18px 18px 30px", color: "#fff", boxSizing: "border-box",
      }}>
        {/* Exam context banner */}
        {labParams?.question && (
          <div style={{ background: "rgba(0,229,255,0.07)", border: "1px solid rgba(0,229,255,0.35)", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.55, marginBottom: 16 }}>
            <b style={{ color: "#00e5ff" }}>From the test:</b> {labParams.question}
            {labParams.whyText && <div style={{ color: "#ff8a9b", marginTop: 5 }}>✗ {labParams.whyText}</div>}
            <div style={{ color: "#9fe8c9", marginTop: 5 }}>Adjust u, a, s — run the two speeds and compare.</div>
          </div>
        )}

        {/* Sliders */}
        {[
          { label: `INITIAL SPEED  u = ${fmt(u)} m/s`, val: u, set: setU, min: 0, max: 60, step: 0.5, color: "#ffd23f" },
          { label: `ACCELERATION  a = ${fmt(a)} m/s²`, val: a, set: setA, min: -15, max: 15, step: 0.5, color: "#3eb6ff" },
          { label: `DISTANCE  s = ${fmt(s, 0)} m`, val: s, set: setS, min: 50, max: 600, step: 10, color: "#a855f7" },
        ].map(({ label, val, set, min, max, step, color }) => (
          <div key={label} style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 11, color, fontWeight: 700, letterSpacing: 1 }}>{label}</label>
            <input type="range" min={min} max={max} step={step} value={val}
              onChange={(e) => { set(parseFloat(e.target.value)); setResult(null); }}
              style={{ width: "100%" }} />
          </div>
        ))}

        {/* Live formula readouts */}
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, lineHeight: 2, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
          <div>v² = u² + 2as</div>
          <div>v = √({fmt(u)}² + 2·{fmt(a)}·{fmt(s, 0)})</div>
          <div style={{ color: "#00e5ff", fontWeight: 700 }}>  = {fmt(vFinal)} m/s</div>
          <div style={{ marginTop: 4, color: "#9aa4b2" }}>time ≈ {fmt(tTotal)} s</div>
          {stopDist !== null && (
            <div style={{ color: "#ff4d6d", marginTop: 4 }}>stop dist = u²/2|a| = {fmt(stopDist, 0)} m</div>
          )}
        </div>

        {/* v-t graph */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: "#9aa4b2", marginBottom: 6, letterSpacing: 1 }}>REAL-TIME v–t GRAPH</div>
          <SuvatGraph history={history} />
          <div style={{ fontSize: 10, color: "rgba(148,163,184,0.7)", textAlign: "center", marginTop: 4 }}>
            slope = a · area = displacement
          </div>
        </div>

        {/* Run button(s) */}
        <button onClick={() => runSim(u, a, s)} disabled={running}
          style={{ width: "100%", padding: "13px", marginBottom: 8, background: running ? "rgba(255,255,255,0.05)" : "linear-gradient(135deg,#00e5ff,#0ea5e9)", border: "none", borderRadius: 10, color: running ? "#5a6478" : "#001a2e", fontWeight: 800, fontSize: 14, cursor: running ? "not-allowed" : "pointer" }}>
          {running ? "⏳ Running…" : `▶ RUN  u=${fmt(u)} a=${fmt(a)} s=${fmt(s, 0)}`}
        </button>
        {labParams?.suvatUWrong != null && (
          <button onClick={() => runSim(labParams.suvatUWrong, a, s)} disabled={running}
            style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(255,77,109,0.2)", border: "1px solid rgba(255,77,109,0.5)", borderRadius: 10, color: "#ff8a9b", fontWeight: 700, fontSize: 13.5, cursor: running ? "not-allowed" : "pointer" }}>
            💥 Run YOUR answer  u = {fmt(labParams.suvatUWrong)} m/s
          </button>
        )}
        {labParams?.suvatUCorrect != null && (
          <button onClick={() => runSim(labParams.suvatUCorrect, a, s)} disabled={running}
            style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(0,255,170,0.15)", border: "1px solid rgba(0,255,170,0.5)", borderRadius: 10, color: "#00ffaa", fontWeight: 700, fontSize: 13.5, cursor: running ? "not-allowed" : "pointer" }}>
            ✓ Run CORRECT  u = {fmt(labParams.suvatUCorrect)} m/s
          </button>
        )}
        {labParams?.suvatDWrong != null && (
          <button onClick={() => runSim(u, a, labParams.suvatDWrong)} disabled={running}
            style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(255,77,109,0.2)", border: "1px solid rgba(255,77,109,0.5)", borderRadius: 10, color: "#ff8a9b", fontWeight: 700, fontSize: 13.5, cursor: running ? "not-allowed" : "pointer" }}>
            💥 Run YOUR stop distance = {fmt(labParams.suvatDWrong, 0)} m
          </button>
        )}
        {labParams?.suvatDCorrect != null && (
          <button onClick={() => runSim(u, a, labParams.suvatDCorrect)} disabled={running}
            style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(0,255,170,0.15)", border: "1px solid rgba(0,255,170,0.5)", borderRadius: 10, color: "#00ffaa", fontWeight: 700, fontSize: 13.5, cursor: running ? "not-allowed" : "pointer" }}>
            ✓ Run CORRECT stop distance = {fmt(labParams.suvatDCorrect, 0)} m
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main PhysicsLab component ────────────────────────────────────
export default function PhysicsLab() {
  const labParams = useGame((s) => s.labParams);
  const closeLab = useGame((s) => s.closeLab);

  const defaultTab = labParams?.vertical ? "loop" : "flat";
  const [tab, setTab] = useState(defaultTab);

  const [vertical, setVertical] = useState(!!labParams?.vertical);
  const [r, setR] = useState(labParams?.r ?? 40);
  const [mu, setMu] = useState(labParams?.mu ?? 0.4);
  const [v, setV] = useState(labParams?.vWrong ?? 15);
  const [runState, setRunState] = useState({ running: false, v: 15, runId: 0 });
  const [status, setStatus] = useState(null);

  useEffect(() => () => Sound.stopAll(), []);

  const vmaxFlat = Math.sqrt(mu * G * r);
  const vTopMin = Math.sqrt(G * r);
  const vBottomMin = Math.sqrt(5 * G * r);
  const needed = (v * v) / r;
  const available = mu * G;

  const run = useCallback((speed) => {
    initAudio();
    setV(speed);
    setStatus("running");
    setRunState((s) => ({ running: true, v: speed, runId: s.runId + 1 }));
  }, []);

  const onResult = useCallback((res) => {
    setStatus(res);
    if (res === "hold") Sound.playChime();
    if (res !== "running") setRunState((s) => ({ ...s, running: res === "slide" }));
  }, []);

  const fmt = (n, d = 1) => Number(n).toFixed(d);
  const narrow = typeof window !== "undefined" && window.innerWidth < 880;

  const TAB_STYLE = (active, color) => ({
    flex: 1, padding: "10px", borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: "pointer",
    background: active ? `linear-gradient(135deg,${color},${color}99)` : "rgba(255,255,255,0.06)",
    color: active ? "#02131a" : "#9aa4b2", border: "none",
  });

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#02030a", zIndex: 50,
      fontFamily: "'Inter', system-ui, sans-serif",
      display: "flex", flexDirection: "column",
    }}>
      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        padding: "10px 16px", background: "rgba(10,12,24,0.98)",
        borderBottom: "1px solid rgba(255,255,255,0.12)",
        flexShrink: 0,
      }}>
        <button onClick={() => { Sound.stopAll(); closeLab(); }}
          style={{ padding: "9px 16px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, color: "#fff", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
          ← {labParams?.fromExam ? "Back to test" : "Back to game"}
        </button>

        <div style={{ flex: 1, display: "flex", gap: 8 }}>
          <button onClick={() => { setTab("flat"); setVertical(false); setStatus(null); }}
            style={TAB_STYLE(tab === "flat" && !vertical, "#3eb6ff")}>
            ⭕ Flat Circle
          </button>
          <button onClick={() => { setTab("loop"); setVertical(true); setStatus(null); if (r > 60) setR(40); }}
            style={TAB_STYLE(tab === "loop", "#e879f9")}>
            🎡 Vertical Loop
          </button>
        </div>

        <div style={{ fontSize: 11, color: "#3eb6ff", fontWeight: 700, letterSpacing: 3 }}>🔬 PHYSICS LAB</div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        <>
          {/* 3D viewport */}
          <div style={{ flex: 1, position: "relative" }}>
            <Canvas camera={{ position: [0, 70, 110], fov: 55, near: 0.1, far: 2000 }}>
              <LabScene r={r} mu={mu} vertical={tab === "loop"} runState={runState} onResult={onResult} />
              </Canvas>

              <div style={{ position: "absolute", top: 14, left: 16, color: "#fff", pointerEvents: "none" }}>
                <div style={{ fontSize: 11, letterSpacing: 4, color: "#3eb6ff", fontWeight: 700 }}>🔬 PHYSICS LAB</div>
                <div style={{ fontSize: 19, fontWeight: 800 }}>
                  {tab === "loop" ? "Vertical Loop Rig" : "Circular Track Rig"}
                </div>
              </div>

              {status && status !== "running" && (
                <div style={{
                  position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)",
                  maxWidth: "min(520px, 80%)", padding: "10px 16px", borderRadius: 10,
                  fontWeight: 700, fontSize: 13.5, textAlign: "center",
                  background: status === "hold" ? "rgba(0,40,25,0.92)" : "rgba(45,8,16,0.92)",
                  border: `1px solid ${status === "hold" ? "rgba(0,255,170,0.5)" : "rgba(255,77,109,0.5)"}`,
                  color: status === "hold" ? "#00ffaa" : "#ff8a9b",
                }}>
                  {tab === "loop"
                    ? status === "hold"
                      ? `✅ FULL LOOP! Entry ${fmt(runState.v)} ≥ √(5·g·r) = ${fmt(vBottomMin)} m/s — contact held even at the top.`
                      : status === "rollback"
                        ? `↩ ROLLED BACK — not enough energy to climb the loop. Need entry ≥ √(5·g·r) = ${fmt(vBottomMin)} m/s.`
                        : `💥 LOST CONTACT on the upper half! At the top you need v ≥ √(g·r) = ${fmt(vTopMin)} m/s — enter with at least √(5·g·r) = ${fmt(vBottomMin)} m/s.`
                    : status === "hold"
                      ? `✅ HELD THE CIRCLE — needed ${fmt(needed)} ≤ available ${fmt(available)} m/s².`
                      : `💥 SLID OFF! needed v²/r = ${fmt(needed)} > available μ·g = ${fmt(available)} m/s².`}
                </div>
              )}
            </div>

            {/* Control panel */}
            <div style={{
              width: narrow ? "100%" : 360, maxHeight: "100%",
              overflowY: "auto", background: "rgba(10,12,24,0.97)",
              borderLeft: "1px solid rgba(255,255,255,0.12)",
              padding: "18px 20px", color: "#fff", boxSizing: "border-box",
            }}>
              {labParams?.question && (
                <div style={{ background: "rgba(232,121,249,0.07)", border: "1px solid rgba(232,121,249,0.35)", borderRadius: 10, padding: "10px 12px", fontSize: 12.5, lineHeight: 1.55, marginBottom: 16 }}>
                  <b style={{ color: "#e879f9" }}>From the test:</b> {labParams.question}
                  {labParams.whyText && <div style={{ color: "#ff8a9b", marginTop: 5 }}>✗ {labParams.whyText}</div>}
                  <div style={{ color: "#9fe8c9", marginTop: 5 }}>Run both speeds — watch the physics decide.</div>
                </div>
              )}

              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, color: "#3eb6ff", fontWeight: 700, letterSpacing: 1 }}>
                  RADIUS r = {fmt(r, 0)} m
                </label>
                <input type="range" min={15} max={tab === "loop" ? 60 : 150} step={1} value={Math.min(r, tab === "loop" ? 60 : 150)}
                  onChange={(e) => setR(parseFloat(e.target.value))} style={{ width: "100%" }} />
              </div>
              {tab === "flat" && (
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: "#ff8a3d", fontWeight: 700, letterSpacing: 1 }}>
                    FRICTION μ = {fmt(mu, 2)}
                  </label>
                  <input type="range" min={0.05} max={1.0} step={0.05} value={mu}
                    onChange={(e) => setMu(parseFloat(e.target.value))} style={{ width: "100%" }} />
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, color: "#ffd23f", fontWeight: 700, letterSpacing: 1 }}>
                  {tab === "loop" ? "ENTRY SPEED (bottom)" : "SPEED"} v = {fmt(v, 1)} m/s
                </label>
                <input type="range" min={2} max={tab === "loop" ? 90 : 45} step={0.5} value={v}
                  onChange={(e) => setV(parseFloat(e.target.value))} style={{ width: "100%" }} />
              </div>

              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, lineHeight: 2, marginBottom: 10, background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "10px 12px" }}>
                {tab === "loop" ? (
                  <>
                    <div>v_min at top = √(g·r) = <b style={{ color: "#ffd23f" }}>{fmt(vTopMin)}</b> m/s</div>
                    <div>v_min at bottom = √(5·g·r) = <b style={{ color: "#00ffaa" }}>{fmt(vBottomMin)}</b> m/s</div>
                    <div>your entry v = <b style={{ color: v >= vBottomMin ? "#00ffaa" : "#ff4d6d" }}>{fmt(v)}</b> m/s</div>
                  </>
                ) : (
                  <>
                    <div>needed a_c = v²/r = <b style={{ color: needed > available ? "#ff4d6d" : "#00ffaa" }}>{fmt(needed)}</b> m/s²</div>
                    <div>available = μ·g = <b style={{ color: "#3eb6ff" }}>{fmt(available)}</b> m/s²</div>
                    <div>v_max = √(μ·g·r) = <b style={{ color: "#ffd23f" }}>{fmt(vmaxFlat)}</b> m/s</div>
                  </>
                )}
              </div>
              {tab === "flat" && (
                <div style={{ position: "relative", height: 12, background: "rgba(255,255,255,0.08)", borderRadius: 6, overflow: "hidden", marginBottom: 14 }}>
                  <div style={{ position: "absolute", inset: 0, width: `${Math.min(100, (needed / available) * 100)}%`, background: needed > available ? "linear-gradient(90deg,#ffd23f,#ff4d6d)" : "linear-gradient(90deg,#00ffaa,#ffd23f)", transition: "width 0.15s" }} />
                </div>
              )}

              <button onClick={() => run(v)}
                style={{ width: "100%", padding: "13px", marginBottom: 8, background: "linear-gradient(135deg,#ffd23f,#ff8a3d)", border: "none", borderRadius: 10, color: "#1a0d00", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
                ▶ RUN at {fmt(v, 1)} m/s
              </button>
              {labParams?.vWrong != null && (
                <button onClick={() => run(labParams.vWrong)}
                  style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(255,77,109,0.2)", border: "1px solid rgba(255,77,109,0.5)", borderRadius: 10, color: "#ff8a9b", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}>
                  💥 Run YOUR answer ({fmt(labParams.vWrong, 1)} m/s)
                </button>
              )}
              {labParams?.vCorrect != null && (
                <button onClick={() => run(labParams.vCorrect)}
                  style={{ width: "100%", padding: "12px", marginBottom: 8, background: "rgba(0,255,170,0.15)", border: "1px solid rgba(0,255,170,0.5)", borderRadius: 10, color: "#00ffaa", fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}>
                  ✓ Run the CORRECT value ({fmt(labParams.vCorrect, 1)} m/s)
                </button>
              )}
              {status === "running" && (
                <div style={{ fontSize: 12.5, color: "#9aa4b2", textAlign: "center", marginTop: 6 }}>
                  Watching the physics…
                </div>
              )}
            </div>
        </>
      </div>
    </div>
  );
}
